// Root-only console: UI text is hardcoded English by design, no i18n keys here.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { SideDrawer } from "../../../atoms/SideDrawer";
import { CustomButton } from "../../../atoms/CustomButton";
import WhatsAppMessagePreview from "./WhatsAppMessagePreview";
import { useUpsertWhatsappLibraryTemplate } from "../../../../hooks/providers";
import { bodyEdgesOnVariable } from "../../../../utils/whatsappText";

type LangCode = "fr" | "en";
const ALL_LANGS: LangCode[] = ["fr", "en"];
const KEY_RE = /^[a-z0-9_]{3,60}$/;
const BUTTON_TITLE_MAX = 25;

export interface EditorButton {
  type: string;
  title: string | Record<string, string>;
}

export interface EditorTemplate {
  key: string;
  category: string;
  hasMedia: boolean;
  languages: string[];
  variables: { key: string; label: string }[];
  body: Record<string, string>;
  samples?: Record<string, string[]> | null;
  sample_image_url?: string | null;
  buttons?: EditorButton[] | null;
}

interface Props {
  show: boolean;
  onClose: () => void;
  customer: string;
  entity: string;
  initial: EditorTemplate | null; // null = new model
  onDelete?: (key: string) => void; // remove the definition from the catalogue (edit mode only)
}

const highestVar = (
  bodies: Record<string, string>,
  langs: LangCode[]
): number => {
  let max = 0;
  langs.forEach((l) => {
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    let m;
    while ((m = re.exec(bodies[l] || "")) !== null)
      max = Math.max(max, Number(m[1]));
  });
  return max;
};

const LibraryTemplateEditor: React.FC<Props> = ({
  show,
  onClose,
  customer,
  entity,
  initial,
  onDelete,
}) => {
  const { mutate: upsert, isLoading } = useUpsertWhatsappLibraryTemplate();
  const isEdit = Boolean(initial);

  const [key, setKey] = useState("");
  const [category, setCategory] = useState<"marketing" | "utility">("utility");
  const [langs, setLangs] = useState<LangCode[]>(["fr", "en"]);
  const [body, setBody] = useState<Record<string, string>>({ fr: "", en: "" });
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [samples, setSamples] = useState<
    Record<string, Record<string, string>>
  >({ fr: {}, en: {} });
  const [hasMedia, setHasMedia] = useState(false);
  const [sampleImageUrl, setSampleImageUrl] = useState("");
  const [hasPaymentButton, setHasPaymentButton] = useState(false);
  const [buttonTitles, setButtonTitles] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!show) return;
    if (initial) {
      setKey(initial.key);
      setCategory(initial.category === "marketing" ? "marketing" : "utility");
      setLangs(
        (initial.languages as LangCode[]).filter((l) => ALL_LANGS.includes(l))
      );
      setBody({ fr: initial.body?.fr || "", en: initial.body?.en || "" });
      setLabels(
        Object.fromEntries(
          (initial.variables || []).map((v) => [v.key, v.label])
        )
      );
      const s: Record<string, Record<string, string>> = { fr: {}, en: {} };
      ALL_LANGS.forEach((l) => {
        (initial.samples?.[l] || []).forEach((val, i) => {
          s[l][String(i + 1)] = val;
        });
      });
      setSamples(s);
      setHasMedia(Boolean(initial.hasMedia));
      setSampleImageUrl(initial.sample_image_url || "");
      const stored = (initial.buttons || []).find((b) => b.type === "payment");
      setHasPaymentButton(Boolean(stored));
      setButtonTitles(
        stored
          ? Object.fromEntries(
              ALL_LANGS.map((l) => [
                l,
                typeof stored.title === "string"
                  ? stored.title
                  : stored.title?.[l] || "",
              ])
            )
          : {}
      );
    } else {
      setKey("");
      setCategory("utility");
      setLangs(["fr", "en"]);
      setBody({ fr: "", en: "" });
      setLabels({});
      setSamples({ fr: {}, en: {} });
      setHasMedia(false);
      setSampleImageUrl("");
      setHasPaymentButton(false);
      setButtonTitles({});
    }
  }, [show, initial]);

  const varCount = useMemo(() => highestVar(body, langs), [body, langs]);

  const previewLang: LangCode = langs[0] || "fr";

  // The server refuses a button on anything but a utility template.
  const buttonAllowed = category === "utility";
  const buttonEnabled = hasPaymentButton && buttonAllowed;
  const buttonErrorFor = (l: LangCode): string | null => {
    if (!buttonEnabled) return null;
    const label = (buttonTitles[l] || "").trim();
    if (!label) return `The ${l.toUpperCase()} button label is required.`;
    if (label.length > BUTTON_TITLE_MAX)
      return `The ${l.toUpperCase()} button label must not exceed ${BUTTON_TITLE_MAX} characters.`;
    return null;
  };
  const buttonErrors = langs
    .map((l) => buttonErrorFor(l))
    .filter(Boolean) as string[];

  const previewButtons = useMemo(
    () =>
      buttonEnabled
        ? [
            {
              type: "url" as const,
              title: (buttonTitles[previewLang] || "").trim(),
            },
          ]
        : [],
    [buttonEnabled, buttonTitles, previewLang]
  );
  const varKeys = useMemo(
    () => Array.from({ length: varCount }, (_, i) => String(i + 1)),
    [varCount]
  );

  const keyValid = KEY_RE.test(key.trim());
  const bodyErrors = langs
    .map((l) => {
      const text = (body[l] || "").trim();
      if (!text) return `The ${l.toUpperCase()} body is required.`;
      if (bodyEdgesOnVariable(text))
        return `The ${l.toUpperCase()} body cannot start or end with a variable.`;
      return null;
    })
    .filter(Boolean) as string[];
  const mediaError = hasMedia && !/^https:\/\//i.test(sampleImageUrl.trim());
  const canSave =
    keyValid &&
    langs.length > 0 &&
    bodyErrors.length === 0 &&
    buttonErrors.length === 0 &&
    !mediaError &&
    !isLoading;

  const previewBody = useMemo(() => {
    let out = body[previewLang] || "";
    varKeys.forEach((k) => {
      const val = samples[previewLang]?.[k] || labels[k] || `{{${k}}}`;
      out = out.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"), val);
    });
    return out;
  }, [body, previewLang, varKeys, samples, labels]);

  const toggleLang = (l: LangCode) =>
    setLangs((prev) =>
      prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]
    );

  const buildDefinition = () => {
    const variables = varKeys.map((k) => ({
      key: k,
      label: labels[k]?.trim() || `Variable ${k}`,
    }));
    const outSamples: Record<string, string[]> = {};
    langs.forEach((l) => {
      const row = varKeys.map((k) => samples[l]?.[k] || "");
      if (varCount > 0 && row.every((v) => v.trim().length > 0))
        outSamples[l] = row;
    });
    const def: Record<string, any> = {
      category,
      hasMedia,
      languages: langs,
      variables,
      body: Object.fromEntries(langs.map((l) => [l, (body[l] || "").trim()])),
    };
    if (Object.keys(outSamples).length) def.samples = outSamples;
    if (hasMedia) def.sample_image_url = sampleImageUrl.trim();
    if (buttonEnabled)
      def.buttons = [
        {
          type: "payment",
          title: Object.fromEntries(
            langs.map((l) => [l, (buttonTitles[l] || "").trim()])
          ),
        },
      ];
    return def;
  };

  const handleSave = () => {
    upsert(
      {
        customer_identification: customer,
        entity_identification: entity,
        key: key.trim(),
        definition: buildDefinition(),
      },
      {
        onSuccess: () => {
          toast.success(
            `Model "${key.trim()}" saved. Use "Generate / submit all" to create it on the WABA.`
          );
          onClose();
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || err.message);
        },
      }
    );
  };

  const field =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none";

  return (
    <SideDrawer
      open={show}
      onClose={onClose}
      title={isEdit ? "Edit template" : "New template"}
    >
      <div className="space-y-4 p-1">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Key
          </span>
          <input
            className={field}
            value={key}
            disabled={isEdit}
            onChange={(e) => setKey(e.target.value)}
            placeholder="e.g. seasonal_sale"
          />
          {!isEdit && key.length > 0 && !keyValid && (
            <span className="mt-1 block text-xs text-red-600">
              3-60 lowercase letters, digits or underscores.
            </span>
          )}
          {isEdit && (
            <span className="mt-1 block text-xs text-gray-500">
              The key is permanent. Editing an already-declared model does not
              change the live Meta template: delete and re-submit it.
            </span>
          )}
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Category
          </span>
          <select
            className={field}
            value={category}
            onChange={(e) => setCategory(e.target.value as any)}
          >
            <option value="utility">utility</option>
            <option value="marketing">marketing</option>
          </select>
        </label>

        <div>
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Languages
          </span>
          <div className="flex gap-4">
            {ALL_LANGS.map((l) => (
              <label
                key={l}
                className="flex items-center gap-2 text-sm text-gray-600"
              >
                <input
                  type="checkbox"
                  checked={langs.includes(l)}
                  onChange={() => toggleLang(l)}
                  className="h-4 w-4"
                />
                {l.toUpperCase()}
              </label>
            ))}
          </div>
        </div>

        {langs.map((l) => (
          <label key={l} className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Body ({l.toUpperCase()})
            </span>
            <textarea
              className={`${field} min-h-[90px]`}
              maxLength={1024}
              value={body[l] || ""}
              onChange={(e) =>
                setBody((prev) => ({ ...prev, [l]: e.target.value }))
              }
              placeholder={`Hello {{1}}, ... .`}
            />
          </label>
        ))}
        <p className="text-xs text-gray-500">
          Use {"{{1}}"}, {"{{2}}"}, ... for variables. *bold*, _italic_. Do not
          end on a variable.
        </p>
        {bodyErrors.map((e) => (
          <span key={e} className="block text-xs text-red-600">
            {e}
          </span>
        ))}

        {varCount > 0 && (
          <div className="space-y-3">
            <div>
              <span className="block text-sm font-medium text-gray-700">
                Variables ({varCount})
              </span>
              <span className="block text-xs text-gray-500">
                For each {"{{n}}"} in the body: a label (what the merchant sees)
                and one example value per language (only used for Meta's review,
                never sent to real recipients).
              </span>
            </div>
            {varKeys.map((k) => (
              <div key={k} className="rounded-lg border border-gray-200 p-3">
                <div className="mb-2 text-xs font-semibold text-gray-700">{`Variable {{${k}}}`}</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <label className="block">
                    <span className="mb-1 block text-xs text-gray-500">
                      Label (merchant-facing)
                    </span>
                    <input
                      className={field}
                      value={labels[k] || ""}
                      onChange={(e) =>
                        setLabels((prev) => ({ ...prev, [k]: e.target.value }))
                      }
                      placeholder="e.g. Customer name"
                    />
                  </label>
                  {langs.map((l) => (
                    <label key={l} className="block">
                      <span className="mb-1 block text-xs text-gray-500">
                        {l.toUpperCase()} example (Meta review)
                      </span>
                      <input
                        className={field}
                        value={samples[l]?.[k] || ""}
                        onChange={(e) =>
                          setSamples((prev) => ({
                            ...prev,
                            [l]: { ...prev[l], [k]: e.target.value },
                          }))
                        }
                        placeholder={l === "fr" ? "ex. Marie" : "e.g. Marie"}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={hasMedia}
            onChange={(e) => setHasMedia(e.target.checked)}
            className="h-4 w-4"
          />
          Image header
        </label>
        {hasMedia && (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Sample image URL
            </span>
            <span className="mb-1 block text-xs text-gray-500">
              A public https URL of an image already hosted on the SUM CDN
              (cdn.1cdn.app). It is only the sample shown to Meta for approval;
              merchants supply their own image when sending.
            </span>
            <input
              type="url"
              className={field}
              value={sampleImageUrl}
              onChange={(e) => setSampleImageUrl(e.target.value)}
              placeholder="https://cdn.1cdn.app/application/UMD/....png"
            />
            {sampleImageUrl.trim() &&
              /^https:\/\//i.test(sampleImageUrl.trim()) && (
                <img
                  src={sampleImageUrl.trim()}
                  alt=""
                  className="mt-2 h-32 w-full max-w-xs rounded-lg border border-gray-300 object-cover"
                />
              )}
            {mediaError && (
              <span className="mt-1 block text-xs text-red-600">
                A public https sample image URL is required.
              </span>
            )}
          </label>
        )}

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={buttonEnabled}
              disabled={!buttonAllowed}
              onChange={(e) => setHasPaymentButton(e.target.checked)}
              className="h-4 w-4"
            />
            Payment button
          </label>
          {buttonAllowed ? (
            buttonEnabled && (
              <div className="space-y-3 rounded-lg border border-gray-200 p-3">
                <span className="block text-xs text-gray-500">
                  One label per declared language, {BUTTON_TITLE_MAX} characters
                  at most. The link itself is built when sending, from the
                  environment's payment domain and the link chosen then.
                </span>
                {langs.map((l) => {
                  const error = buttonErrorFor(l);
                  return (
                    <label key={l} className="block">
                      <span className="mb-1 block text-xs text-gray-500">
                        {l.toUpperCase()} label
                      </span>
                      <input
                        className={field}
                        value={buttonTitles[l] || ""}
                        onChange={(e) =>
                          setButtonTitles((prev) => ({
                            ...prev,
                            [l]: e.target.value,
                          }))
                        }
                        placeholder={l === "fr" ? "ex. Payer" : "e.g. Pay now"}
                      />
                      {error && (
                        <span className="mt-1 block text-xs text-red-600">
                          {error}
                        </span>
                      )}
                    </label>
                  );
                })}
                <CustomButton
                  variant="secondary"
                  size="xs"
                  title="Remove button"
                  onClick={() => setHasPaymentButton(false)}
                />
              </div>
            )
          ) : (
            <span className="block text-xs text-gray-500">
              Call-to-action buttons are only allowed on utility templates.
            </span>
          )}
        </div>

        <div>
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Preview ({previewLang.toUpperCase()})
          </span>
          <WhatsAppMessagePreview
            imageUrl={hasMedia ? sampleImageUrl || null : null}
            body={previewBody}
            isMarketing={category === "marketing"}
            buttons={previewButtons}
          />
        </div>

        <div className="flex items-center justify-between gap-2 pt-2">
          <div>
            {isEdit && onDelete && (
              <CustomButton
                variant="danger"
                onClick={() => onDelete(key.trim())}
                disabled={isLoading}
                title="Delete template"
              />
            )}
          </div>
          <div className="flex gap-2">
            <CustomButton
              variant="secondary"
              onClick={onClose}
              title="Cancel"
            />
            <CustomButton
              variant="primary"
              onClick={handleSave}
              disabled={!canSave}
              isLoading={isLoading}
              title="Save"
            />
          </div>
        </div>
      </div>
    </SideDrawer>
  );
};

export default LibraryTemplateEditor;
